import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowLeft, 
  ChefHat,
  Heart,
  Sparkles,
  Clock,
  Shield,
  Camera,
  Award,
  CheckCircle,
  Utensils,
  Droplet,
  Package,
  MessageCircle,
  AlertTriangle
} from 'lucide-react';

export default function HomeCookCode() {
  const navigate = useNavigate();

  const sections = [
    {
      title: 'The "Kitchen-First" Hygiene Standard',
      subtitle: 'The kitchen is the heart of the home, but for our platform, it is a professional workspace.',
      icon: Sparkles,
      color: 'text-amber-500',
      items: [
        {
          title: 'Personal Grooming',
          description: 'Our Home Chefs always wear a clean apron and hairnet/head-cover while cooking. Nails must be trimmed and unpainted.'
        },
        {
          title: 'The 20-Second Rule',
          description: 'Hands must be washed with soap before starting, after handling raw meat/eggs, and after any interruption (like answering the door).'
        },
        {
          title: 'Zero-Contamination Zone',
          description: 'Use separate cutting boards for vegetables and meats. Never use the same spoon for "tasting" and "stirring."'
        }
      ]
    },
    {
      title: 'Ingredient Integrity',
      subtitle: 'The buyer is paying for "home-made" quality, which means better ingredients than a commercial restaurant.',
      icon: Utensils,
      color: 'text-green-500',
      items: [
        {
          title: 'Freshness First',
          description: 'No stale oil or "leftover" ingredients from previous days.'
        },
        {
          title: 'The "Pantry" Transparency',
          description: 'Cooks must be willing to list the brands of oil, salt, and masala they use if asked.'
        },
        {
          title: 'Water Quality',
          description: 'Only RO-purified or boiled water should be used for cooking and cleaning vegetables.'
        }
      ]
    },
    {
      title: 'The "Right Feel" Packaging',
      subtitle: 'The packaging is the first thing the neighbour sees. It should look neat and intentional.',
      icon: Package,
      color: 'text-blue-500',
      items: [
        {
          title: 'Eco-Friendly Focus',
          description: 'Whenever possible, use biodegradable or recyclable containers.'
        },
        {
          title: 'The Seal of Trust',
          description: 'Every "Thali" box or container must be sealed with a RuchiBox sticker or a handwritten note to show it hasn\'t been opened since it left the kitchen.'
        },
        {
          title: 'Temperature Control',
          description: 'Hot food must be packed in insulated containers or wrapped to ensure it doesn\'t arrive lukewarm.'
        }
      ]
    },
    {
      title: 'Communication & Punctuality',
      subtitle: 'In a community, your word is your bond.',
      icon: MessageCircle,
      color: 'text-purple-500',
      items: [
        {
          title: 'Real-time Alerts',
          description: 'If a dish is going to be 10 minutes late, the cook must notify the platform immediately.'
        },
        {
          title: 'Allergen Honesty',
          description: 'Clearly state if the dish contains peanuts, dairy, or gluten. Never "assume" it\'s okay.'
        }
      ]
    },
    {
      title: 'Visual Transparency (The "Digital Window")',
      subtitle: 'Since the buyer can\'t see the kitchen, the app must show it.',
      icon: Camera,
      color: 'text-pink-500',
      items: [
        {
          title: 'Weekly Kitchen Photos',
          description: 'Cooks are encouraged to upload a photo of their cleaned prep area once a week.'
        },
        {
          title: 'The "Chef\'s Greeting"',
          description: 'A small handwritten note like "Cooked with love by Kavitha for House #402" creates a powerful psychological bond of trust.'
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Button
              variant="ghost"
              onClick={() => navigate('/promise')}
              className="pl-0"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center">
                <ChefHat className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-bold">Home-Cook Code</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center mx-auto mb-6 shadow-lg">
            <ChefHat className="w-12 h-12 text-white" />
          </div>
          <h1 className="font-heading text-4xl font-bold text-foreground mb-4">
            Home-Cook Code of Conduct
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            These standards define what makes a RuchiBox Home Cook special. 
            Every dish carries not just flavors, but trust and care.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section, idx) => (
            <Card key={idx} className="overflow-hidden">
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 p-6 border-b">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full bg-white shadow flex items-center justify-center ${section.color}`}>
                    <section.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-foreground">
                      {section.title}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {section.subtitle}
                    </p>
                  </div>
                </div>
              </div>
              <CardContent className="p-6">
                <ul className="space-y-4">
                  {section.items.map((item, itemIdx) => (
                    <li key={itemIdx} className="flex gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-foreground">{item.title}</h4>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Verified Badge Section */}
        <Card className="mt-12 border-2 border-primary/20 bg-primary/5">
          <CardContent className="p-8 text-center">
            <Award className="w-16 h-16 text-primary mx-auto mb-4" />
            <h3 className="font-heading text-2xl font-bold text-foreground mb-3">
              Become a Verified Home-Cook
            </h3>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
              To get the &quot;Verified Home-Cook&quot; badge on RuchiBox, you must:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left max-w-2xl mx-auto">
              <div className="flex items-start gap-2">
                <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">1</div>
                <span className="text-sm">Sign this Code of Conduct</span>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">2</div>
                <span className="text-sm">Upload a 30-second video of your kitchen</span>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">3</div>
                <span className="text-sm">Complete a basic hygiene quiz</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
